import {
  EditOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  LockOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { message } from "antd";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Modal } from "../../../shared";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import AppContext from "../../../../context/AppContext";
import {
  GAME_PASS_DROPDOWN_LABELS,
  SIDER_TABS,
} from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import {
  createEmptyMonthlyChallenge,
  duplicateMonthlyChallenge,
  reorderMonthlyChallenges,
  type MonthlyChallengeDraft,
} from "../monthlyChallengeData";
import MonthlyChallengeCard, {
  type MonthlyChallengeCardDragProps,
} from "../Challenges/MonthlyChallengeCard";
import {
  MONTHLY_SCHEDULE_INITIAL_VALUES,
  resolveMonthlyAutoStartAnchor,
} from "../monthlyScheduleData";
import { useGetCampaign } from "../../../../queries/game-pass/useGetCampaign";
import { useGetCampaignsList } from "../../../../queries/game-pass/useGetCampaignsList";
import { useGetChallengesList } from "../../../../queries/game-pass/useGetChallengesList";
import { useGetTasksList } from "../../../../queries/game-pass/useGetTasksList";
import { useUpdateChallenge } from "../../../../queries/game-pass/useUpdateChallenge";
import { useUpdateCampaignTask } from "../../../../queries/game-pass/useUpdateCampaignTask";
import { useCreateCampaignChallenge } from "../../../../queries/game-pass/useCreateCampaignChallenge";
import { useCreateCampaignTask } from "../../../../queries/game-pass/useCreateCampaignTask";
import { useDeleteChallenge } from "../../../../queries/game-pass/useDeleteChallenge";
import { useDeleteTask } from "../../../../queries/game-pass/useDeleteTask";
import { useUpdateCampaign } from "../../../../queries/game-pass/useUpdateCampaign";
import {
  useCompleteCampaign,
  useScheduleCampaign,
  useUnscheduleCampaign,
} from "../../../../queries/game-pass/useCampaignLifecycle";
import { mapApiCampaignToMonthlyDetails } from "../../../../queries/game-pass/monthlyDetailsMapper";
import {
  buildStreakResetHourPatch,
  mapCampaignWindowToMonthlySchedule,
  mapMonthlyChallengeToChallengeRequest,
  mapMonthlyScheduleToUpdateCampaignRequest,
} from "../../../../queries/game-pass/monthlyCampaignMappers";
import { mapMonthlyChallengeToTaskRequest } from "../../../../queries/game-pass/monthlyTaskMapper";
import {
  buildCampaignScheduleConflictMessage,
  findCampaignScheduleConflict,
} from "../../../../queries/game-pass/campaignScheduleConflict";
import {
  buildMonthlyScheduleReadinessMessage,
  findMonthlyScheduleReadinessIssue,
} from "../../../../queries/game-pass/monthlyScheduleReadiness";
import {
  CAMPAIGN_API_STATUS,
  type CampaignTaskListRecord,
  type UpdateCampaignRequest,
} from "../../../../types/gamePass";
import EditMonthlyChallengeModal, {
  type EditMonthlyChallengeValues,
} from "./EditMonthlyChallengeModal/EditMonthlyChallengeModal";
import {
  findMonthlyDetailsById,
  type MonthlyDetailsData,
} from "./monthlyDetailsData";

import styles from "./MonthlyDetails.module.css";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

/** No-op handler passed to locked (Active) challenge cards. Their inputs are
 *  disabled and the header exposes no Edit / Save / Delete, so none of the
 *  card's callbacks ever fire — but the props are required, so we route them
 *  all through one shared stub rather than inventing per-card handlers. */
const noop = (): void => undefined;

/** Audience metric tile in the locked (Active) General header — mirrors the
 *  Seasons Details layout. `value` renders the number; when omitted the tile
 *  shows just its label (the All Reports tile until its reports view ships). */
const MetricTile: FC<{
  label: string;
  value?: string;
  testId: string;
}> = ({ label, value, testId }) => (
  <div className={styles.metricTile} data-testid={testId}>
    <span className={styles.metricLabel}>{label}</span>
    {value !== undefined && <span className={styles.metricValue}>{value}</span>}
  </div>
);

const MonthlyDetails: FC = () => {
  const navigate = useNavigate();
  const { challengeId } = useParams<{ challengeId?: string }>();
  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.GAME_PASS, GAME_PASS_DROPDOWN_LABELS.MONTHLY);
  }, [setActiveSidebarTab]);

  // Load the campaign + its challenge groups + tiered tasks from the reused
  // season endpoints (keyed on `campaign_id`). The single-campaign GET drives
  // the General header; the challenge / task lists re-hydrate the cards.
  const campaignQuery = useGetCampaign(challengeId);
  const challengesQuery = useGetChallengesList(
    challengeId ? { campaignId: challengeId } : undefined,
  );
  const tasksQuery = useGetTasksList<CampaignTaskListRecord>(
    challengeId ? { campaignId: challengeId } : undefined,
  );

  // Edit-in-place persistence for a draft/scheduled campaign's existing
  // challenge groups + tiered tasks. `PUT challenge` carries name/order/
  // description; `PUT task` carries every task field + the tier ladder.
  const updateChallenge = useUpdateChallenge();
  const updateCampaignTask = useUpdateCampaignTask();

  // Create-on-save for an in-session card (Add Challenge / duplicate) under a
  // live campaign: POST the challenge group then POST its tiered task, mirroring
  // the Create wizard's sequential create. The returned ids are stamped back on
  // the card so a later edit PUTs and delete hits the API instead of re-creating.
  const createChallenge = useCreateCampaignChallenge();
  const createTask = useCreateCampaignTask();

  // Deletes a single challenge group + its tiered task. API-seeded cards carry
  // `serverChallengeId` / `serverTaskId` and are removed on the backend (DELETE
  // task then DELETE challenge); in-session cards (Add Challenge / duplicate)
  // have no backend record and are dropped from local state only. The pending
  // id drives a destructive confirm modal (mirrors End / Unschedule).
  const deleteChallenge = useDeleteChallenge();
  const deleteTask = useDeleteTask();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Ends (completes) an Active campaign. The BE `/complete` transition is
  // body-less, so a single-click destructive confirm is enough — no audit
  // fields like Seasons' production End modal.
  const completeCampaign = useCompleteCampaign();
  const [isEndOpen, setIsEndOpen] = useState(false);

  // Pulls a Scheduled campaign back to draft. `/unschedule` is body-less too,
  // so it reuses the same single-click confirm pattern as End.
  const unscheduleCampaign = useUnscheduleCampaign();
  const [isUnscheduleOpen, setIsUnscheduleOpen] = useState(false);

  // Writes a campaign's window (`updateCampaign`) and, for a Draft, transitions
  // it out of draft (`scheduleCampaign`) — the same two calls the Create
  // wizard's final step makes. The campaigns list feeds the overlap guard. Both
  // are driven from the single edit popup below.
  const updateCampaign = useUpdateCampaign();
  const scheduleCampaign = useScheduleCampaign();
  const campaignsListQuery = useGetCampaignsList();

  // Single pencil-edit popup for a Draft / Scheduled campaign's General card. It
  // exposes the challenge Name + the Schedule window, so both renaming and
  // scheduling happen here — there's no separate Schedule affordance:
  //   • Scheduled → the primary "Save" re-times / renames in place (`PUT`) and
  //                 stays scheduled.
  //   • Draft     → the primary action reads "Schedule": it writes the window
  //                 then promotes the campaign out of `draft`.
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editValues, setEditValues] = useState<EditMonthlyChallengeValues>({
    name: "",
    schedule: MONTHLY_SCHEDULE_INITIAL_VALUES,
  });

  // The currently ACTIVE monthly challenge this one auto-starts after — drives
  // the Schedule modal's start auto-fill (active end + 1 min) and pre-filled
  // 3-day end window (FE-2374). The campaign being edited is excluded so it can
  // never anchor to itself.
  const autoStartAnchor = useMemo(
    () =>
      resolveMonthlyAutoStartAnchor(
        campaignsListQuery.data?.campaigns ?? [],
        challengeId,
      ),
    [campaignsListQuery.data?.campaigns, challengeId],
  );

  const details = useMemo<MonthlyDetailsData | undefined>(() => {
    if (campaignQuery.data) {
      return mapApiCampaignToMonthlyDetails(
        campaignQuery.data,
        challengesQuery.data?.challenges ?? [],
        tasksQuery.data?.tasks ?? [],
      );
    }
    // Fallback to the Figma placeholder records for the two mock ids
    // (dev / QA deep-links) until every environment has live campaigns.
    return findMonthlyDetailsById(challengeId);
  }, [campaignQuery.data, challengesQuery.data, tasksQuery.data, challengeId]);
  usePageTitle(
    details?.general.name ?? AppStrings.MONTHLY_CONFIGURATION_PAGE_TITLE,
  );

  // Challenges are held in local state so editing (expand/collapse, inline
  // field edits), duplicate, delete and reorder are interactive. There is no
  // monthly admin *edit* API yet, so edits live only for the session — exactly
  // like the Create Monthly Challenge wizard. The per-challenge editor is the
  // wizard's `MonthlyChallengeCard`, reused verbatim so the add and edit flows
  // never drift on fields/validation.
  const [challenges, setChallenges] = useState<
    ReadonlyArray<MonthlyChallengeDraft>
  >([]);

  // Seed the local challenge list once per resolved record, but only after the
  // challenge / task lists have settled — otherwise the first seed (campaign
  // resolves before its challenges) would lock in an empty list. `seededRef`
  // guards against re-seeding on background refetches, which would otherwise
  // discard the admin's in-session edits.
  const seededDetailsIdRef = useRef<string | undefined>(undefined);
  const detailsId = details?.id;
  const challengesReady =
    !challengeId ||
    (!challengesQuery.isLoading && !challengesQuery.isPlaceholderData);
  const tasksReady =
    !challengeId || (!tasksQuery.isLoading && !tasksQuery.isPlaceholderData);

  // For a *live* campaign the cards must not be seeded until both the challenge
  // and task lists have actually loaded — not merely stopped loading. A failed
  // or aborted list fetch leaves `isLoading`/`isPlaceholderData` false with no
  // data, which still satisfied the "ready" gate above and seeded cards missing
  // their `serverChallengeId` / `serverTaskId`. Because the seed runs once per
  // record, that lost-id state stuck for the whole visit and silently
  // downgraded every edit to a local collapse and every delete to a local
  // removal — no PUT / DELETE ever fired (the intermittent "sometimes no API
  // call" report, tracking whichever fetch happened to fail). Requiring loaded
  // data defers the seed until React Query's retry succeeds, so a re-hydrated
  // card always carries the ids that route Save → PUT and Delete → DELETE. The
  // mock deep-link fallback (no live campaign) needs no backend ids, so it is
  // unaffected.
  const isLiveCampaign = Boolean(campaignQuery.data);
  const apiListsLoaded =
    !isLiveCampaign ||
    (challengesQuery.data !== undefined &&
      !challengesQuery.isError &&
      tasksQuery.data !== undefined &&
      !tasksQuery.isError);

  useEffect(() => {
    if (!details) return;
    if (!challengesReady || !tasksReady) return;
    if (!apiListsLoaded) return;
    if (seededDetailsIdRef.current === detailsId) return;
    seededDetailsIdRef.current = detailsId;
    setChallenges(details.challenges);
  }, [details, detailsId, challengesReady, tasksReady, apiListsLoaded]);

  // Only one challenge may be actively edited at a time — an open, never-saved
  // draft blocks adding/duplicating another (mirrors the wizard). A saved
  // challenge that has been re-opened doesn't count, so it can always collapse.
  const hasUnsavedDraft = useMemo(
    () =>
      challenges.some(
        (challenge) => challenge.isExpanded && !challenge.isSaved,
      ),
    [challenges],
  );

  const handleBack = useCallback(() => {
    navigate(NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_MONTHLY);
  }, [navigate]);

  const handleOpenEnd = useCallback(() => setIsEndOpen(true), []);
  const handleCancelEnd = useCallback(() => setIsEndOpen(false), []);
  const handleConfirmEnd = useCallback(async () => {
    // Only Active campaigns render the End affordance, and those always resolve
    // from a live GET (never the deep-linked mocks), so `challengeId` is set.
    if (!challengeId) {
      setIsEndOpen(false);
      handleBack();
      return;
    }
    try {
      await completeCampaign.mutateAsync(challengeId);
      setIsEndOpen(false);
      message.success(AppStrings.MONTHLY_DETAILS_END_SUCCESS);
      // The campaign is now `completed` — bounce back to the listing rather
      // than re-render this page into its read-only completed state.
      handleBack();
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.MONTHLY_DETAILS_END_ERROR),
      );
    }
  }, [challengeId, completeCampaign, handleBack]);

  const handleOpenUnschedule = useCallback(() => setIsUnscheduleOpen(true), []);
  const handleCancelUnschedule = useCallback(
    () => setIsUnscheduleOpen(false),
    [],
  );
  const handleConfirmUnschedule = useCallback(async () => {
    // Only Scheduled campaigns render the Unschedule affordance, and those
    // always resolve from a live GET, so `challengeId` is set.
    if (!challengeId) {
      setIsUnscheduleOpen(false);
      handleBack();
      return;
    }
    try {
      await unscheduleCampaign.mutateAsync(challengeId);
      setIsUnscheduleOpen(false);
      message.success(AppStrings.MONTHLY_DETAILS_UNSCHEDULE_SUCCESS);
      // Now a draft — bounce back to the listing rather than re-render here.
      handleBack();
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.MONTHLY_DETAILS_UNSCHEDULE_ERROR),
      );
    }
  }, [challengeId, unscheduleCampaign, handleBack]);

  // Whether the campaign being viewed is still a draft — drives the edit
  // popup's primary-action label ("Schedule" vs "Save") and its save behaviour
  // (promote vs update-in-place). A draft is the only editable status that
  // still needs to be scheduled; the mock deep-link records expose it via the
  // General card's `scheduleEnabled` flag.
  const isDraftCampaign = campaignQuery.data
    ? campaignQuery.data.status === CAMPAIGN_API_STATUS.DRAFT
    : Boolean(details?.general.scheduleEnabled);

  const isSavingEdit = updateCampaign.isPending || scheduleCampaign.isPending;

  // Seed the edit popup and open it. A Scheduled campaign pre-fills its live
  // window so the admin can re-time it; a Draft opens with empty pickers (its
  // Step-1 placeholder window isn't a real schedule) so the admin authors the
  // window that promotes it.
  const handleOpenEdit = useCallback(() => {
    const schedule =
      campaignQuery.data &&
      campaignQuery.data.status !== CAMPAIGN_API_STATUS.DRAFT
        ? mapCampaignWindowToMonthlySchedule(
            campaignQuery.data.start_date,
            campaignQuery.data.end_date,
          )
        : { ...MONTHLY_SCHEDULE_INITIAL_VALUES };
    setEditValues({ name: details?.general.name ?? "", schedule });
    setIsEditOpen(true);
  }, [campaignQuery.data, details?.general.name]);

  const handleCloseEdit = useCallback(() => setIsEditOpen(false), []);

  const handleSaveEdit = useCallback(
    async (next: EditMonthlyChallengeValues) => {
      // The mock deep-link records have no backend campaign to update — reflect
      // the edit locally (the not-persisted behavior) and close.
      if (!challengeId || !campaignQuery.data) {
        setEditValues(next);
        setIsEditOpen(false);
        return;
      }

      const isDraft = campaignQuery.data.status === CAMPAIGN_API_STATUS.DRAFT;

      // A draft can only be scheduled once it's structurally complete: at least
      // one challenge, and every challenge fully authored (no empty tier
      // fields). Surface the first offending challenge by name and bail before
      // any write so nothing is committed for an incomplete campaign (mirrors
      // Season's `findSeasonScheduleReadinessIssue`). Re-timing an already
      // scheduled campaign skips this — its challenges are already live.
      if (isDraft) {
        const readinessIssue = findMonthlyScheduleReadinessIssue(challenges);
        if (readinessIssue) {
          message.error(buildMonthlyScheduleReadinessMessage(readinessIssue));
          return;
        }
      }

      // Block a window that overlaps an existing active/scheduled campaign
      // before any write. This matters both when scheduling a draft (its window
      // becomes live) and when re-timing an already-scheduled campaign; the
      // campaign itself is excluded so its own window never counts.
      const conflict = findCampaignScheduleConflict({
        schedule: next.schedule,
        existingCampaigns: campaignsListQuery.data?.campaigns ?? [],
        excludeCampaignId: challengeId,
      });
      if (conflict) {
        message.error(buildCampaignScheduleConflictMessage(conflict));
        return;
      }

      const body: UpdateCampaignRequest = {
        ...mapMonthlyScheduleToUpdateCampaignRequest(next.schedule),
        name: next.name.trim(),
      };

      // Step 1 — persist name / window. Wrapped on its own so a failure here
      // (nothing committed) surfaces the plain update error and bails, without
      // being conflated with the separate scheduling call below.
      try {
        await updateCampaign.mutateAsync({ campaignId: challengeId, body });
      } catch (error) {
        message.error(
          getApiErrorMessage(error, AppStrings.GAME_PASS_MONTHLY_DETAILS_EDIT_ERROR),
        );
        return;
      }

      if (!isDraft) {
        // Scheduled edit: stay on the page. Both `updateCampaign` and the
        // lifecycle hooks patch this campaign's detail cache (via
        // `mergeCampaignIntoLists`), so the General header reflects the new
        // name / window immediately without a refetch flash.
        setIsEditOpen(false);
        message.success(AppStrings.GAME_PASS_MONTHLY_DETAILS_EDIT_SUCCESS);
        return;
      }

      // Step 2 (Draft only) — promote out of `draft`, the same transition the
      // Create wizard's final step makes, then bounce back to the listing. The
      // name / window are ALREADY committed by step 1, so a failure here is a
      // partial success: tell the admin their edits were saved and only the
      // scheduling failed, rather than a bare "failed to schedule" that implies
      // nothing stuck (they'd otherwise re-enter everything).
      try {
        await scheduleCampaign.mutateAsync(challengeId);
        setIsEditOpen(false);
        message.success(AppStrings.MONTHLY_DETAILS_SCHEDULE_SUCCESS);
        handleBack();
      } catch {
        message.error(AppStrings.GAME_PASS_MONTHLY_DETAILS_SCHEDULE_PARTIAL_ERROR);
      }
    },
    [
      challengeId,
      campaignQuery.data,
      campaignsListQuery.data,
      challenges,
      updateCampaign,
      scheduleCampaign,
      handleBack,
    ],
  );

  const updateChallengeAt = useCallback(
    (
      index: number,
      updater: (challenge: MonthlyChallengeDraft) => MonthlyChallengeDraft,
    ) => {
      setChallenges((prev) =>
        prev.map((challenge, i) =>
          i === index ? updater(challenge) : challenge,
        ),
      );
    },
    [],
  );

  const handleChallengeChange = useCallback(
    (index: number, next: MonthlyChallengeDraft) => {
      updateChallengeAt(index, () => next);
    },
    [updateChallengeAt],
  );

  // Persist a single challenge card, choosing create vs update *per entity* so
  // an already-persisted group is always updated in place and never duplicated.
  // The challenge group is the source of truth for "does a backend record
  // exist":
  //   • `serverChallengeId` present → PUT the group (name/order/description).
  //   • absent (a fresh Add Challenge / duplicate card) → POST it (create).
  // The tiered task is decided independently:
  //   • `serverTaskId` present → PUT the task (all fields + tiers).
  //   • absent → POST it under the (existing or just-created) group.
  // That last case is the fix for the "sometimes POST, sometimes PUT" report: a
  // persisted group can legitimately have no task yet (an interrupted create
  // left `tasks: []`). The old all-or-nothing gate (`serverChallengeId &&
  // serverTaskId`) misread such a card as brand-new and POSTed a *second*
  // group; repairing the missing task with a create under the existing group
  // keeps the group a single PUT.
  const persistChallenge = useCallback(
    async (challenge: MonthlyChallengeDraft, index: number) => {
      if (!challengeId) return;

      // 1. Challenge group — update if it exists, else create.
      let resolvedChallengeId = challenge.serverChallengeId;
      let createdGroupThisCall = false;
      if (resolvedChallengeId) {
        await updateChallenge.mutateAsync({
          challengeId: resolvedChallengeId,
          body: {
            name: challenge.name.trim(),
            sort_order: index,
            description: challenge.description.trim(),
          },
        });
      } else {
        const group = await createChallenge.mutateAsync(
          mapMonthlyChallengeToChallengeRequest(challenge, challengeId, index),
        );
        // Fail loud if the create response omits the id we need to parent the
        // task (and later PUT/DELETE against) — falling through to a null id
        // would silently POST an unparentable task and orphan the group.
        resolvedChallengeId = group?.challenge_id ?? group?.id ?? undefined;
        if (!resolvedChallengeId) {
          throw new Error("Create challenge response missing challenge id");
        }
        createdGroupThisCall = true;
      }

      // 2. Tiered task — update if it exists, else create under the group. The
      //    task write must carry the campaign/group identity (same as create):
      //    without `campaign_id` the BE task-update handler falls back to a
      //    season lookup and 404s with "Season None not found".
      const taskBody = mapMonthlyChallengeToTaskRequest(challenge, {
        campaignId: challengeId,
        challengeId: resolvedChallengeId,
        challengeName: challenge.name.trim(),
        challengeOrder: index,
      });
      try {
        let resolvedTaskId = challenge.serverTaskId;
        if (resolvedTaskId) {
          await updateCampaignTask.mutateAsync({
            taskId: resolvedTaskId,
            campaignId: challengeId,
            body: taskBody,
          });
        } else {
          // A rejected POST is a genuine failure (handled by the rollback
          // below). A *resolved* POST whose body the tolerant unwrap can't read
          // (FE-1692) is NOT — the task was still created, so throwing here and
          // rolling back would delete a challenge that actually saved (the
          // "POST fires but the challenge is gone on refresh" report). When the
          // id can't be read from the create response, recover it from the
          // source of truth — refetch the tasks list and match on the parent
          // group — so a follow-up edit PUTs the task instead of duplicating it.
          const createdTask = await createTask.mutateAsync(taskBody);
          resolvedTaskId = createdTask?.id ?? createdTask?.task_id ?? undefined;
          if (!resolvedTaskId) {
            try {
              const refetched = await tasksQuery.refetch();
              resolvedTaskId = refetched.data?.tasks?.find(
                (task) => task.challenge_id === resolvedChallengeId,
              )?.id;
            } catch {
              // Refetch failed — leave the id unknown; the next full page load
              // re-hydrates it from the tasks list. The task still exists.
            }
          }
        }
        // Stamp the ids back so subsequent saves PUT and Delete hits the API.
        setChallenges((prev) =>
          prev.map((card) =>
            card.id === challenge.id
              ? {
                  ...card,
                  serverChallengeId: resolvedChallengeId,
                  serverTaskId: resolvedTaskId,
                }
              : card,
          ),
        );
      } catch (err) {
        // Only roll back a group we *created* in this call, and only when the
        // task write genuinely FAILED (the mutation threw). An existing group
        // (PUT) must survive a failed task write so the admin can retry — we
        // must never delete a group that already holds other backend state.
        if (createdGroupThisCall && resolvedChallengeId) {
          try {
            await deleteChallenge.mutateAsync({
              challengeId: resolvedChallengeId,
            });
          } catch {
            // Rollback itself failed — surface the original error below rather
            // than masking it with the cleanup failure.
          }
        }
        throw err;
      }

      // Patch from the full campaign set, substituting this save, so a
      // non-Login-Streak card cannot null a sibling Login Streak's hour.
      const nextChallenges = challenges.map((card) =>
        card.id === challenge.id ? challenge : card,
      );
      const streakResetPatch = buildStreakResetHourPatch(nextChallenges);
      if (Object.keys(streakResetPatch).length > 0) {
        await updateCampaign.mutateAsync({
          campaignId: challengeId,
          body: streakResetPatch,
        });
      }
    },
    [
      challengeId,
      challenges,
      updateChallenge,
      updateCampaignTask,
      updateCampaign,
      createChallenge,
      createTask,
      deleteChallenge,
      tasksQuery,
    ],
  );

  const handleChallengeSave = useCallback(
    (index: number) => {
      const target = challenges[index];
      if (!target) return;
      // The optimistic collapse clears `hasUnsavedDraft`, which re-enables
      // drag-reorder while the write is in flight — so the captured `index` can
      // go stale. Pin the card by its stable id for the error-recovery path.
      const targetId = target.id;
      // Optimistically collapse to the saved summary (matches the wizard).
      updateChallengeAt(index, (challenge) => ({
        ...challenge,
        isSaved: true,
        isExpanded: false,
      }));

      // Without a live campaign (the deep-linked mock records) there's nothing
      // to write to, so the card stays local — the pre-persistence behavior.
      if (!challengeId || !campaignQuery.data) return;

      // An existing group is updated (PUT); a fresh in-session card is created
      // (POST). This drives the toast copy and the error-recovery `isSaved`
      // reset — a failed create was never really saved.
      const isUpdate = Boolean(target.serverChallengeId);

      void (async () => {
        try {
          await persistChallenge(target, index);
          message.success(
            isUpdate
              ? AppStrings.MONTHLY_CHALLENGE_UPDATE_SUCCESS
              : AppStrings.MONTHLY_DETAILS_CREATE_CHALLENGE_SUCCESS,
          );
        } catch (error) {
          message.error(
            getApiErrorMessage(
              error,
              isUpdate
                ? AppStrings.MONTHLY_CHALLENGE_UPDATE_ERROR
                : AppStrings.MONTHLY_DETAILS_CREATE_CHALLENGE_ERROR,
            ),
          );
          // Re-open so the admin can retry — the write wasn't committed. Match by
          // id, not the captured index, since a reorder may have moved the card.
          // A failed create was never saved, so also drop `isSaved`.
          setChallenges((prev) =>
            prev.map((challenge) =>
              challenge.id === targetId
                ? {
                    ...challenge,
                    isExpanded: true,
                    ...(isUpdate ? {} : { isSaved: false }),
                  }
                : challenge,
            ),
          );
        }
      })();
    },
    [
      challenges,
      challengeId,
      campaignQuery.data,
      persistChallenge,
      updateChallengeAt,
    ],
  );

  const handleChallengeEdit = useCallback(
    (index: number) => {
      updateChallengeAt(index, (challenge) => ({
        ...challenge,
        isExpanded: true,
      }));
    },
    [updateChallengeAt],
  );

  const handleChallengeClose = useCallback((index: number) => {
    setChallenges((prev) => {
      const target = prev[index];
      if (!target) return prev;
      // An unsaved draft is discarded on close; a previously-saved challenge
      // just collapses back to its summary.
      if (!target.isSaved) {
        return prev.filter((_, i) => i !== index);
      }
      return prev.map((challenge, i) =>
        i === index ? { ...challenge, isExpanded: false } : challenge,
      );
    });
  }, []);

  // The Delete Challenge button only renders on a saved card, so this always
  // opens the confirm modal rather than deleting inline.
  const handleRequestDelete = useCallback((id: string) => {
    setPendingDeleteId(id);
  }, []);

  const isDeletingChallenge = deleteTask.isPending || deleteChallenge.isPending;

  // Block dismissal (Go Back / Esc / mask / close icon) once the delete is in
  // flight: deleteTask can resolve while deleteChallenge is still running, so
  // closing here would hide the still-running deletion and make a committed
  // delete look cancelled to the admin.
  const handleCancelDelete = useCallback(() => {
    if (isDeletingChallenge) return;
    setPendingDeleteId(null);
  }, [isDeletingChallenge]);

  const pendingDeleteChallenge = useMemo(
    () => challenges.find((challenge) => challenge.id === pendingDeleteId),
    [challenges, pendingDeleteId],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;
    const target = challenges.find(
      (challenge) => challenge.id === pendingDeleteId,
    );
    if (!target) {
      setPendingDeleteId(null);
      return;
    }
    const { serverChallengeId, serverTaskId } = target;
    // In-session card (Add Challenge / duplicate) with no group on the backend:
    // nothing to remove, so drop it from local state — the pre-persistence
    // behavior. A persisted group *without* a task (an interrupted create left
    // `tasks: []`) is NOT local-only — it still has a backend group to delete.
    if (!serverChallengeId) {
      setChallenges((prev) =>
        prev.filter((challenge) => challenge.id !== target.id),
      );
      setPendingDeleteId(null);
      return;
    }
    try {
      // Delete the tiered task first (when one exists), then its parent
      // challenge group — the group can't be removed while a task still
      // references it. A group with no task skips straight to the group delete
      // rather than being stranded on the backend. The modal stays open
      // (showing the spinner) until the calls settle, then the card is dropped
      // and the modal closes; on error it stays open to retry.
      if (serverTaskId) {
        await deleteTask.mutateAsync({ taskId: serverTaskId });
      }
      await deleteChallenge.mutateAsync({ challengeId: serverChallengeId });
      setChallenges((prev) =>
        prev.filter((challenge) => challenge.id !== target.id),
      );
      setPendingDeleteId(null);
      message.success(AppStrings.MONTHLY_DETAILS_DELETE_CHALLENGE_SUCCESS);
    } catch (error) {
      message.error(
        getApiErrorMessage(
          error,
          AppStrings.MONTHLY_DETAILS_DELETE_CHALLENGE_ERROR,
        ),
      );
    }
  }, [pendingDeleteId, challenges, deleteTask, deleteChallenge]);

  const handleDuplicate = useCallback(
    (index: number) => {
      // A copy opens as a new unsaved draft, so block it while one is already
      // open — same invariant the Add Challenge button enforces.
      if (hasUnsavedDraft) return;
      setChallenges((prev) =>
        prev.flatMap((challenge, i) =>
          i === index
            ? [
                challenge,
                duplicateMonthlyChallenge(
                  challenge,
                  AppStrings.CHALLENGE_DUPLICATE_NAME_SUFFIX,
                ),
              ]
            : [challenge],
        ),
      );
    },
    [hasUnsavedDraft],
  );

  const handleAddChallenge = useCallback(() => {
    if (hasUnsavedDraft) return;
    setChallenges((prev) => [...prev, createEmptyMonthlyChallenge()]);
  }, [hasUnsavedDraft]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const challengeIds = useMemo(
    () => challenges.map((challenge) => challenge.id),
    [challenges],
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setChallenges((prev) => {
      const next = reorderMonthlyChallenges(
        prev,
        String(active.id),
        String(over.id),
      );
      return next === prev ? prev : next;
    });
  }, []);

  if (
    !details &&
    (campaignQuery.isLoading ||
      challengesQuery.isLoading ||
      tasksQuery.isLoading)
  ) {
    return (
      <>
        <div className={styles.page} data-testid="monthly-details-loading">
          <div className={styles.header}>
            <button
              type="button"
              className={styles.backButton}
              aria-label={AppStrings.MONTHLY_DETAILS_BACK_ARIA}
              onClick={handleBack}
              data-testid="monthly-details-back"
            >
              <LeftOutlined className={styles.backButtonIcon} />
            </button>
          </div>
          <div className={styles.notFound}>
            <p className={styles.notFoundDescription}>
              {AppStrings.MONTHLY_CONFIGURATION_LOADING}
            </p>
          </div>
        </div>
      </>
    );
  }

  if (!details) {
    return (
      <>
        <div className={styles.page} data-testid="monthly-details">
          <div className={styles.header}>
            <button
              type="button"
              className={styles.backButton}
              aria-label={AppStrings.MONTHLY_DETAILS_BACK_ARIA}
              onClick={handleBack}
              data-testid="monthly-details-back"
            >
              <LeftOutlined className={styles.backButtonIcon} />
            </button>
          </div>
          <div className={styles.notFound} data-testid="monthly-details-not-found">
            <h2 className={styles.notFoundTitle}>
              {AppStrings.MONTHLY_DETAILS_NOT_FOUND_TITLE}
            </h2>
            <p className={styles.notFoundDescription}>
              {AppStrings.MONTHLY_DETAILS_NOT_FOUND_DESCRIPTION}
            </p>
          </div>
        </div>
      </>
    );
  }

  const { general } = details;
  const isLocked = details.mode === "locked";
  const enrolledDisplay =
    general.enrolled !== undefined
      ? general.enrolled.toLocaleString()
      : undefined;
  const avgCompletionDisplay =
    general.avgCompletionPct !== undefined
      ? `${general.avgCompletionPct}%`
      : undefined;

  return (
    <>
      <div className={styles.page} data-testid="monthly-details">
        {/* ── Breadcrumb + back ─────────────────────────────────────────── */}
        <div className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            aria-label={AppStrings.MONTHLY_DETAILS_BACK_ARIA}
            onClick={handleBack}
            data-testid="monthly-details-back"
          >
            <LeftOutlined className={styles.backButtonIcon} />
          </button>
          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <button
              type="button"
              className={styles.breadcrumbLink}
              onClick={handleBack}
              data-testid="monthly-details-breadcrumb-monthly"
            >
              {AppStrings.MONTHLY_DETAILS_BREADCRUMB}
            </button>
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <span
              className={styles.breadcrumbCurrent}
              data-testid="monthly-details-breadcrumb-current"
            >
              {general.name}
            </span>
          </nav>
        </div>

        {/* ── General section ───────────────────────────────────────────── */}
        <section
          className={styles.section}
          aria-labelledby="monthly-details-general-heading"
          data-testid="monthly-details-general"
        >
          <h2
            id="monthly-details-general-heading"
            className={styles.sectionHeading}
          >
            {AppStrings.MONTHLY_DETAILS_GENERAL}
          </h2>
          {isLocked ? (
            // Active view: wide summary tile (name + Locked badge, Active pill,
            // dates, inert End link) alongside the three audience metric tiles.
            <div className={styles.generalGrid}>
              <div className={styles.generalTileWide}>
                <div className={styles.generalTitleRow}>
                  <span className={styles.generalName}>{general.name}</span>
                  <span
                    className={styles.generalLockedBadge}
                    data-testid="monthly-details-locked-badge"
                  >
                    <LockOutlined
                      className={styles.generalLockedBadgeIcon}
                      aria-hidden="true"
                    />
                    {AppStrings.SEASON_CARD_LOCKED}
                    <InfoCircleOutlined
                      className={styles.generalLockedBadgeInfo}
                      aria-label={AppStrings.MONTHLY_CHALLENGE_LOCKED_INFO}
                      role="img"
                    />
                  </span>
                </div>
                <span
                  className={styles.generalStatusActive}
                  data-testid="monthly-details-status"
                >
                  <span
                    className={styles.generalStatusDot}
                    aria-hidden="true"
                  />
                  {general.status.label}
                </span>
                <span
                  className={styles.generalDates}
                  data-testid="monthly-details-dates"
                >
                  {general.dateRange}
                </span>
                {general.endEnabled && (
                  <button
                    type="button"
                    className={styles.endButton}
                    aria-label={AppStrings.MONTHLY_DETAILS_END_ARIA}
                    onClick={handleOpenEnd}
                    title={AppStrings.MONTHLY_DETAILS_END_ARIA}
                    data-testid="monthly-details-end"
                  >
                    {AppStrings.MONTHLY_DETAILS_END}
                  </button>
                )}
              </div>
              <MetricTile
                label={AppStrings.SEASON_DETAILS_METRIC_ENROLLED}
                value={enrolledDisplay}
                testId="monthly-details-metric-enrolled"
              />
              <MetricTile
                label={AppStrings.SEASON_DETAILS_METRIC_AVG_COMPLETION}
                value={avgCompletionDisplay}
                testId="monthly-details-metric-avg-completion"
              />
              {/* The "See all" link is intentionally omitted until the
                  reports view ships — a wired-to-noop button reads as
                  functional but does nothing, so we render the tile without
                  an affordance rather than a dead one. */}
              <MetricTile
                label={AppStrings.SEASON_DETAILS_METRIC_ALL_REPORTS}
                testId="monthly-details-metric-all-reports"
              />
            </div>
          ) : (
            <div className={styles.generalCard}>
              <div className={styles.generalTitleRow}>
                <span className={styles.generalName}>{general.name}</span>
                <button
                  type="button"
                  className={styles.generalEditButton}
                  aria-label={AppStrings.MONTHLY_DETAILS_EDIT_GENERAL_ARIA}
                  onClick={handleOpenEdit}
                  title={AppStrings.MONTHLY_DETAILS_EDIT_GENERAL_ARIA}
                  data-testid="monthly-details-edit-general"
                >
                  <EditOutlined aria-hidden="true" />
                </button>
              </div>
              <span
                className={styles.generalStatus}
                data-testid="monthly-details-status"
              >
                <span className={styles.generalStatusLabel}>
                  {general.status.label}
                </span>
                {general.countdown && (
                  <>
                    <span className={styles.generalStatusCountdown}>
                      {"("}
                      {`${general.countdown.prefix} `}
                      <strong className={styles.generalStatusCountdownValue}>
                        {general.countdown.value}
                      </strong>
                      {")"}
                    </span>
                    <InfoCircleOutlined
                      className={styles.generalStatusInfoIcon}
                      aria-label={AppStrings.MONTHLY_DETAILS_LOCKS_INFO_ARIA}
                      role="img"
                      data-testid="monthly-details-status-info"
                    />
                  </>
                )}
              </span>
              <span
                className={styles.generalDates}
                data-testid="monthly-details-dates"
              >
                {general.dateRange}
              </span>
              {general.unscheduleEnabled && (
                <button
                  type="button"
                  className={styles.unscheduleButton}
                  aria-label={AppStrings.MONTHLY_DETAILS_UNSCHEDULE_ARIA}
                  onClick={handleOpenUnschedule}
                  title={AppStrings.MONTHLY_DETAILS_UNSCHEDULE_ARIA}
                  data-testid="monthly-details-unschedule"
                >
                  {AppStrings.MONTHLY_DETAILS_UNSCHEDULE}
                </button>
              )}
            </div>
          )}
        </section>

        {/* ── Challenges section ────────────────────────────────────────── */}
        <section
          className={styles.section}
          aria-labelledby="monthly-details-challenges-heading"
          data-testid="monthly-details-challenges"
        >
          <h2
            id="monthly-details-challenges-heading"
            className={styles.sectionHeading}
          >
            {AppStrings.MONTHLY_DETAILS_CHALLENGES_HEADING}
          </h2>

          {isLocked ? (
            // Active view: challenges are read-only — no drag-reorder wrapper
            // and no Add Challenge affordance. Each card renders in locked mode.
            <ul
              className={styles.challengeList}
              aria-label={AppStrings.MONTHLY_CHALLENGE_LIST_ARIA}
            >
              {challenges.map((challenge, index) => (
                <li key={challenge.id} className={styles.challengeListItem}>
                  <MonthlyChallengeCard
                    challenge={challenge}
                    index={index}
                    mode="locked"
                    onChange={noop}
                    onDuplicate={noop}
                    onDelete={noop}
                    onSave={noop}
                    onEdit={noop}
                    onClose={noop}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={challengeIds}
                  strategy={verticalListSortingStrategy}
                >
                  <ul
                    className={styles.challengeList}
                    aria-label={AppStrings.MONTHLY_CHALLENGE_LIST_ARIA}
                  >
                    {challenges.map((challenge, index) => (
                      <SortableMonthlyChallengeCard
                        key={challenge.id}
                        challenge={challenge}
                        index={index}
                        onChange={(next) => handleChallengeChange(index, next)}
                        onDuplicate={() => handleDuplicate(index)}
                        onDelete={() => handleRequestDelete(challenge.id)}
                        onSave={() => handleChallengeSave(index)}
                        onEdit={() => handleChallengeEdit(index)}
                        onClose={() => handleChallengeClose(index)}
                        disableDuplicate={hasUnsavedDraft}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>

              <button
                type="button"
                className={styles.addChallengeButton}
                onClick={handleAddChallenge}
                disabled={hasUnsavedDraft}
                title={
                  hasUnsavedDraft
                    ? AppStrings.ADD_CHALLENGE_FINISH_DRAFT_FIRST
                    : undefined
                }
                data-testid="monthly-details-add-challenge"
              >
                <PlusOutlined className={styles.addChallengeIcon} />
                {AppStrings.ADD_CHALLENGE}
              </button>
            </>
          )}
        </section>

        <Modal
          open={isEndOpen}
          onClose={handleCancelEnd}
          intent="destructive"
          size="sm"
          destroyOnHidden
          title={
            <span className={styles.endModalTitle}>
              {AppStrings.MONTHLY_DETAILS_END_CONFIRM_TITLE}
            </span>
          }
          footer={
            <div className={styles.endModalFooter}>
              <Button
                intent="neutral"
                variant="outline"
                onClick={handleCancelEnd}
                data-testid="monthly-details-end-go-back"
              >
                {AppStrings.MONTHLY_DETAILS_END_CONFIRM_GO_BACK}
              </Button>
              <Button
                intent="danger"
                variant="outline"
                style={{ color: "var(--COLOR-LOST_COLOR)" }}
                onClick={handleConfirmEnd}
                disabled={completeCampaign.isPending}
                loading={completeCampaign.isPending}
                data-testid="monthly-details-end-confirm"
              >
                {AppStrings.MONTHLY_DETAILS_END_CONFIRM_CTA}
              </Button>
            </div>
          }
        >
          <div data-testid="monthly-details-end-modal">
            <p className={styles.endModalBody}>
              {AppStrings.MONTHLY_DETAILS_END_CONFIRM_BODY_PREFIX}
              <span className={styles.endModalName}>{`"${general.name}"`}</span>
              {AppStrings.MONTHLY_DETAILS_END_CONFIRM_BODY_SUFFIX}
            </p>
          </div>
        </Modal>

        <Modal
          open={isUnscheduleOpen}
          onClose={handleCancelUnschedule}
          intent="destructive"
          size="sm"
          destroyOnHidden
          title={
            <span className={styles.endModalTitle}>
              {AppStrings.MONTHLY_DETAILS_UNSCHEDULE_CONFIRM_TITLE}
            </span>
          }
          footer={
            <div className={styles.endModalFooter}>
              <Button
                intent="neutral"
                variant="outline"
                onClick={handleCancelUnschedule}
                data-testid="monthly-details-unschedule-go-back"
              >
                {AppStrings.MONTHLY_DETAILS_UNSCHEDULE_CONFIRM_GO_BACK}
              </Button>
              <Button
                intent="danger"
                variant="outline"
                style={{ color: "var(--COLOR-LOST_COLOR)" }}
                onClick={handleConfirmUnschedule}
                disabled={unscheduleCampaign.isPending}
                loading={unscheduleCampaign.isPending}
                data-testid="monthly-details-unschedule-confirm"
              >
                {AppStrings.MONTHLY_DETAILS_UNSCHEDULE_CONFIRM_CTA}
              </Button>
            </div>
          }
        >
          <div data-testid="monthly-details-unschedule-modal">
            <p className={styles.endModalBody}>
              {AppStrings.MONTHLY_DETAILS_UNSCHEDULE_CONFIRM_BODY_PREFIX}
              <span className={styles.endModalName}>{`"${general.name}"`}</span>
              {AppStrings.MONTHLY_DETAILS_UNSCHEDULE_CONFIRM_BODY_SUFFIX}
            </p>
          </div>
        </Modal>

        <EditMonthlyChallengeModal
          open={isEditOpen}
          challengeName={general.name}
          initialValues={editValues}
          onSave={handleSaveEdit}
          onClose={handleCloseEdit}
          isSaving={isSavingEdit}
          saveLabel={
            isDraftCampaign
              ? AppStrings.MONTHLY_DETAILS_SCHEDULE_CTA
              : AppStrings.GAME_PASS_MONTHLY_DETAILS_EDIT_SAVE
          }
          autoStartAnchor={autoStartAnchor}
        />

        <Modal
          open={pendingDeleteId !== null}
          onClose={handleCancelDelete}
          intent="destructive"
          size="sm"
          destroyOnHidden
          closable={!isDeletingChallenge}
          maskClosable={!isDeletingChallenge}
          keyboard={!isDeletingChallenge}
          title={
            <span className={styles.endModalTitle}>
              {AppStrings.MONTHLY_DETAILS_DELETE_CHALLENGE_CONFIRM_TITLE}
            </span>
          }
          footer={
            <div className={styles.endModalFooter}>
              <Button
                intent="neutral"
                variant="outline"
                onClick={handleCancelDelete}
                disabled={isDeletingChallenge}
                data-testid="monthly-details-delete-challenge-go-back"
              >
                {AppStrings.MONTHLY_DETAILS_DELETE_CHALLENGE_CONFIRM_GO_BACK}
              </Button>
              <Button
                intent="danger"
                variant="outline"
                style={{ color: "var(--COLOR-LOST_COLOR)" }}
                onClick={handleConfirmDelete}
                disabled={isDeletingChallenge}
                loading={isDeletingChallenge}
                data-testid="monthly-details-delete-challenge-confirm"
              >
                {AppStrings.MONTHLY_DETAILS_DELETE_CHALLENGE_CONFIRM_CTA}
              </Button>
            </div>
          }
        >
          <div data-testid="monthly-details-delete-challenge-modal">
            <p className={styles.endModalBody}>
              {AppStrings.MONTHLY_DETAILS_DELETE_CHALLENGE_CONFIRM_BODY_PREFIX}
              <span className={styles.endModalName}>
                {`"${pendingDeleteChallenge?.name ?? ""}"`}
              </span>
              {AppStrings.MONTHLY_DETAILS_DELETE_CHALLENGE_CONFIRM_BODY_SUFFIX}
            </p>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default MonthlyDetails;

type SortableMonthlyChallengeCardProps = {
  challenge: MonthlyChallengeDraft;
  index: number;
  onChange: (next: MonthlyChallengeDraft) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onSave: () => void;
  onEdit: () => void;
  onClose: () => void;
  disableDuplicate: boolean;
};

/**
 * Wraps {@link MonthlyChallengeCard} so it participates in the parent
 * `<SortableContext>` — derives the transform + activator wiring from
 * `useSortable` and hands them to the card as `drag`, keeping the card free of
 * any `@dnd-kit/*` imports. Same wrapper the wizard's challenge step uses.
 */
const SortableMonthlyChallengeCard: FC<SortableMonthlyChallengeCardProps> = ({
  challenge,
  ...rest
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: challenge.id });

  const drag: MonthlyChallengeCardDragProps = {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transformStyle: {
      transform: CSS.Transform.toString(transform),
      transition,
    },
    isDragging,
  };

  return (
    <li className={styles.challengeListItem}>
      <MonthlyChallengeCard challenge={challenge} drag={drag} {...rest} />
    </li>
  );
};
